/* eslint-disable no-underscore-dangle */
import Application from './Application';
import { hasPath } from './utils';
import MFEContext from './MFEContext';

declare global {
  interface Window {
    MFE: any;
  }
}

export default class MicroFE {
  defaultPath = '/';

  static instance: any;

  // eslint-disable-next-line react/static-property-placement
  context: MFEContext = {};

  private _applications: { [name: string]: Application } = {};

  constructor(context = {}) {
    // eslint-disable-next-line no-extra-boolean-cast
    if (!!MicroFE.instance) {
      return MicroFE.instance;
    }
    MicroFE.instance = this;
    this.context = context;
    this._applications = {};

    /**
     * WIP: This is a temporary solution to expose a dispatch command to all
     * applications. This should be an exported utility to be imported by
     * registered applications
     *
     * Simple Example:
     * MFE.dispatchCommand('openDrawer', 'Event');
     */
    window.MFE = {
      dispatchCommand: (command: string, data: any) => {
        (this.context as any).publish(command, data);
      },
    };

    return this;
  }

  get applications() {
    return Object.values(this._applications);
  }

  get hasDefaultPath() {
    return ![undefined, null, '/', ''].includes(this.defaultPath);
  }

  loadConfig = async (url: string): Promise<MicroFE> => {
    try {
      const response = await fetch(url);
      const json = await response.json();
      return await this.registerApps(json);
    } catch (err) {
      return err;
    }
  };

  registerApps = async ({
    applications,
    defaultPath,
  }: MicroApp): Promise<MicroFE> => {
    this.defaultPath = defaultPath || this.defaultPath;
    applications
      .filter((appConfig) => !appConfig.disabled)
      .forEach((appConfig) => this.registerApp(appConfig, hasPath));
    return this;
  };

  registerApp = (
    config: AppConfig,
    validator: AppValidator = hasPath
  ): Application => {
    const app = new Application(config, validator, this.context);
    console.log(`Registered ${app.name}.`);
    this._applications = {
      ...this._applications,
      [config.name]: app,
    };
    return app;
  };

  startAtRoute = (location: Location = window.location) => {
    this.applications.forEach(async (app) => {
      try {
        if (app.isValidRoute(location)) {
          console.log(`Location is valid for ${app.name}`);
          console.log(`Running ${app.name}...`);
          await app.run();
        } else if (app.isRunning()) {
          app.stop();
        }
      } catch (error) {
        console.error(error);
      }
    });
  };

  start = async () => {
    this.startAtRoute();
  };
}
